import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Country, PaymentMethod } from '@/app/enums';
import { formatPrice } from '@/app/utils/formatting';
import { getDonationAmountIncludingTip } from '@/app/utils/numbers';
import { getCountryConfig } from '@/app/utils/countryConfig';

interface DonationFormBaseValidationSchemaSettings {
  isFirstNameRequired: boolean;
  isLastNameRequired: boolean;
  isTipDisabled: boolean;
  areAddressFieldsRequired?: boolean;
  amountMinimum?: number;
  isPhoneNumberRequired?: boolean;
  ignoreTinOrBirthDate?: boolean;
}

interface SingleDonationFormValidationSchemaSettings extends DonationFormBaseValidationSchemaSettings {
  isAuthenticated: boolean;
}

interface LongTermDonationFormValidationSchemaSettings extends DonationFormBaseValidationSchemaSettings {
  isAuthenticated: boolean;
}

interface GoodHabitDonationFormValidationSchemaSettings extends DonationFormBaseValidationSchemaSettings {
  isAuthenticated: boolean;
}

interface CommercialProjectDonationFormValidationSchemaSettings extends DonationFormBaseValidationSchemaSettings {
  isAuthenticated: boolean;
  isNoteRequired: boolean;
}

const countryConfig = getCountryConfig();

const donationAmountMinimum = Number(countryConfig.donationFormCommon.donationAmountMinimum ?? 1);
const donationAmountMaximum = Number(countryConfig.donationFormCommon.donationAmountMaximum ?? 10000000);
const maximumSumForPaymentByCard = Number(countryConfig.donationFormCommon.donateByCardTotalAmountMaximum ?? 100000);

export const useDonationFormValidationSchemaBase = ({
  isFirstNameRequired,
  isLastNameRequired,
  isTipDisabled,
  areAddressFieldsRequired = false,
  amountMinimum,
  isPhoneNumberRequired = false,
  ignoreTinOrBirthDate = false,
}: DonationFormBaseValidationSchemaSettings) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const validationSchemaBase = useMemo(() => {
    const baseSchema = yup.object().shape({
      amount: yup
        .number()
        .required(t('validationErrors.required'))
        .typeError(t('validationErrors.number'))
        .min(
          amountMinimum ?? donationAmountMinimum,
          t('validationErrors.donationAmountMinimum', {
            minimum: formatPrice(amountMinimum ?? donationAmountMinimum),
          }),
        )
        .max(
          donationAmountMaximum,
          t('validationErrors.donationAmountMaximum', { maximum: formatPrice(donationAmountMaximum) }),
        )
        .test(
          'maximumForPaymentByCard',
          t('validationErrors.totalAmountByCardMaximum', {
            maximum: formatPrice(maximumSumForPaymentByCard),
          }),
          function () {
            const isCardPaymentSelected = this.parent.paymentMethod === PaymentMethod.Card;
            const donationAmount = this.parent.amount || 0;
            const tipPercentage = this.parent.tipPercentage || 0;
            const total = getDonationAmountIncludingTip(donationAmount, tipPercentage);

            return !isCardPaymentSelected || total < maximumSumForPaymentByCard;
          },
        ),
      paymentMethod: yup.string().oneOf(Object.values(PaymentMethod)).required(t('validationErrors.required')),
      firstName: yup
        .string()
        .test('firstNameRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !isFirstNameRequired || !!item;
        })
        .max(255, t('validationErrors.maximumStringLength', { maximum: 255 })),
      lastName: yup
        .string()
        .test('lastNameRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !isLastNameRequired || !!item;
        })
        .max(255, t('validationErrors.maximumStringLength', { maximum: 255 })),
      certificateOfGoodness: yup.bool(),
      donationReceipt: yup.bool(),
      noNewsletter: yup.bool(),
      agreedToShareContact: yup.bool(),
      certificateRecipient: yup
        .string()
        .nullable()
        .max(38, t('validationErrors.maximumStringLength', { maximum: 38 }))
        .when('certificateOfGoodness', {
          is: true,
          then: (schema) => schema.required(t('validationErrors.required')),
        }),
      certificateDonor: yup
        .string()
        .nullable()
        .max(38, t('validationErrors.maximumStringLength', { maximum: 38 })),
      nameOnReceipt: yup
        .string()
        .nullable()
        .max(255, t('validationErrors.maximumStringLength', { maximum: 255 }))
        .when('donationReceipt', {
          is: true,
          then: (schema) => schema.required(t('validationErrors.required')),
        }),
      streetAndHouseNumber: yup
        .string()
        .nullable()
        .when('donationReceipt', {
          is: true,
          then: (schema) => schema.required(t('validationErrors.required')),
        })
        .test('streetAndHouseNumberRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !areAddressFieldsRequired || !!item;
        }),
      city: yup
        .string()
        .nullable()
        .when('donationReceipt', {
          is: true,
          then: (schema) => schema.required(t('validationErrors.required')),
        })
        .test('cityRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !areAddressFieldsRequired || !!item;
        }),
      country: yup
        .string()
        .nullable()
        .when('donationReceipt', {
          is: true,
          then: (schema) => schema.oneOf(Object.values(Country)).required(t('validationErrors.required')),
        })
        .test('countryRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !areAddressFieldsRequired || !!item;
        }),
      zipCode: yup
        .string()
        .nullable()
        .when('donationReceipt', {
          is: true,
          then: (schema) => schema.required(t('validationErrors.required')),
        })
        .test('zipCodeRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !areAddressFieldsRequired || !!item;
        }),
      tin: yup
        .string()
        .nullable()
        .test('onOfTinOrBirthDate', t('validationErrors.oneOfTinOrBirthDate'), function () {
          return (
            (!areAddressFieldsRequired && !this.parent.donationReceipt) ||
            !!this.parent.tin ||
            !!this.parent.birthDate ||
            ignoreTinOrBirthDate
          );
        }),
      birthDate: yup
        .string()
        .nullable()
        .test('onOfTinOrBirthDate', t('validationErrors.oneOfTinOrBirthDate'), function () {
          return (
            (!this.parent.donationReceipt && !areAddressFieldsRequired) ||
            !!this.parent.tin ||
            !!this.parent.birthDate ||
            ignoreTinOrBirthDate
          );
        }),
      phoneNumber: yup
        .string()
        .nullable()
        .test('phoneNumberRequiredBasedOnSettings', t('validationErrors.required'), function (item) {
          return !isPhoneNumberRequired || !!item;
        }),
    });

    if (!isTipDisabled) {
      return baseSchema.shape({
        tipPercentage: yup.number().required(t('validationErrors.required')).typeError(t('validationErrors.number')),
        customTipAmount: yup
          .number()
          .typeError(t('validationErrors.number'))
          .min(0, t('validationErrors.tipAmountMinimum')),
      });
    }

    return baseSchema;
  }, [isFirstNameRequired, isLastNameRequired, isTipDisabled, language, amountMinimum, areAddressFieldsRequired, t]);

  return validationSchemaBase;
};

export const useSingleDonationFormValidationSchema = ({
  isAuthenticated,
  isFirstNameRequired,
  isLastNameRequired,
  isTipDisabled,
  isPhoneNumberRequired,
}: SingleDonationFormValidationSchemaSettings) => {
  const { t } = useTranslation();
  const validationSchemaBase = useDonationFormValidationSchemaBase({
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    isPhoneNumberRequired,
  });

  if (!isAuthenticated) {
    return validationSchemaBase.shape({
      email: yup.string().email(t('validationErrors.email')).nullable().required(t('validationErrors.required')),
    });
  }

  return validationSchemaBase;
};

export const useLongTermDonationFormValidationSchema = ({
  isAuthenticated,
  isFirstNameRequired,
  isLastNameRequired,
  isTipDisabled,
  isPhoneNumberRequired,
}: LongTermDonationFormValidationSchemaSettings) => {
  const { t } = useTranslation();
  const validationSchemaBase = useDonationFormValidationSchemaBase({
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    isPhoneNumberRequired,
  });

  const validationSchemaExtended = validationSchemaBase.shape({
    donationMode: yup.string().required(t('validationErrors.required')),
  });

  if (!isAuthenticated) {
    return validationSchemaExtended.shape({
      email: yup.string().email(t('validationErrors.email')).nullable().required(t('validationErrors.required')),
    });
  }

  return validationSchemaExtended;
};

export const useGoodHabitFormValidationSchema = ({
  isAuthenticated,
  isFirstNameRequired,
  isLastNameRequired,
  isTipDisabled,
  isPhoneNumberRequired,
}: GoodHabitDonationFormValidationSchemaSettings) => {
  const { t } = useTranslation();
  const validationSchemaBase = useDonationFormValidationSchemaBase({
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    isPhoneNumberRequired,
  });

  const goodHabitValidationSchema = validationSchemaBase.shape({
    categoryId: yup.string().required(t('validationErrors.required')),
  });

  if (!isAuthenticated) {
    return goodHabitValidationSchema.shape({
      email: yup.string().email(t('validationErrors.email')).nullable().required(t('validationErrors.required')),
    });
  }

  return goodHabitValidationSchema;
};

export const useCommercialProjectDonationFormValidationSchema = ({
  isAuthenticated,
  isFirstNameRequired,
  isLastNameRequired,
  isTipDisabled,
  areAddressFieldsRequired,
  amountMinimum,
  isNoteRequired,
  isPhoneNumberRequired,
}: CommercialProjectDonationFormValidationSchemaSettings) => {
  const { t } = useTranslation();
  const validationSchemaBase = useDonationFormValidationSchemaBase({
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    areAddressFieldsRequired,
    amountMinimum,
    isPhoneNumberRequired,
    ignoreTinOrBirthDate: true,
  });

  const commercialProjectBase = validationSchemaBase.shape({
    note: yup.string().test('noteRequiredBasedOnSettings', t('validationErrors.rewardNoteRequired'), function (item) {
      return !isNoteRequired || !!item;
    }),
  });

  if (!isAuthenticated) {
    return commercialProjectBase.shape({
      email: yup.string().email(t('validationErrors.email')).nullable().required(t('validationErrors.required')),
    });
  }

  return commercialProjectBase;
};
